import { gsap } from 'gsap';
import { nextTick, onMounted, onUnmounted, reactive, watch, type ShallowRef } from 'vue';
import type { MultilinkStoryblok } from '@seb-platform/shared/types/storyblok';
import { useLinks } from './useLinks';
import { useMouseFollow } from './useMouseFollow';

type UtilButtonStoryblokLike = {
  text?: string;
  theme: 'primary' | 'secondary';
  icon?: string;
  link?: MultilinkStoryblok;
};

/**
 * Usage:
 * Simply add the data-cursor attribute to your mouse-sensitive element.
 *
 * Example:
 * ```
 * <div :data-cursor="JSON.stringify({ button: { theme: 'primary', text: 'Button Text' } })"></div>
 * ```
 *
 * The string encoded data is typed as CursorData below.
 */

export interface CursorData {
  button?: UtilButtonStoryblokLike;
  labels?: string[];
}

export function useCursor(container: ShallowRef<HTMLDivElement | null>) {
  const { updatePosition, shouldFollow } = useMouseFollow();
  const { visitLink } = useLinks();

  const moduleState = reactive<{
    currentTarget: HTMLElement | undefined;
    button: UtilButtonStoryblokLike | undefined;
    parsedData: CursorData | undefined;
    isDark: boolean;
    theme: 'primary' | 'secondary';
  }>({
    currentTarget: undefined,
    button: undefined,
    parsedData: undefined,
    isDark: false,
    theme: 'primary',
  });

  let fadeIconTimeout: number;

  function setCursor() {
    document.documentElement.style.cursor = '';
    if (moduleState.currentTarget) {
      moduleState.currentTarget.style.cursor = 'pointer';
    }
  }

  function resetCursor() {
    if (moduleState.currentTarget) {
      moduleState.currentTarget.style.cursor = 'auto';
    }
  }

  const tickerState = reactive<{
    mode: string;
    targetButton: UtilButtonStoryblokLike | undefined;
    progress: number;
    targetText: string;
    labelIndex: number;
  }>({
    mode: 'in',
    targetButton: undefined,
    progress: 0,
    targetText: '',
    labelIndex: 0,
  });

  watch(
    () => tickerState.progress,
    () => {
      const index = Math.round(tickerState.progress * tickerState.targetText.length);
      if (moduleState.button) {
        moduleState.button.text = tickerState.targetText.slice(0, index);
      }
    }
  );

  function checkButton() {
    if (tickerState.targetButton) {
      void animateButton(tickerState.targetButton);
    }
  }

  function clearButton(force: boolean = false) {
    if (tickerState.progress !== 0 && !force) {
      return;
    }
    resetCursor();
    moduleState.parsedData = undefined;
    moduleState.currentTarget = undefined;
    tickerState.targetButton = undefined;
    tickerState.labelIndex = 0;
    tickerState.targetText = '';
    moduleState.button = undefined;
  }

  function checkLabels() {
    const parsedLabels: string[] = moduleState.parsedData?.labels ?? [];
    if (!parsedLabels || parsedLabels.length === 1) {
      return;
    }
    void gsap
      .to(tickerState, {
        duration: 0.25,
        overwrite: true,
        progress: 0,
        delay: 5,
      })
      .then(() => {
        tickerState.labelIndex++;
        if (tickerState.labelIndex > parsedLabels.length - 1) {
          tickerState.labelIndex = 0;
        }
        tickerState.targetButton!.text = parsedLabels[tickerState.labelIndex];
        void animateButton(tickerState.targetButton ?? null);
      });
  }

  function updateButtonStyles() {
    if (!moduleState.button) {
      return;
    }
    if (moduleState.button.icon !== tickerState.targetButton?.icon) {
      moduleState.button.icon = tickerState.targetButton?.icon;
    }
    if (moduleState.button.theme !== tickerState.targetButton?.theme) {
      moduleState.button.theme = tickerState.targetButton!.theme;
    }
  }

  async function animateButton(button: UtilButtonStoryblokLike | null) {
    if (!container.value) {
      return;
    }
    if (button && !moduleState.button) {
      moduleState.button = button;
      await nextTick();
    }
    const buttonEl = container.value.querySelector<HTMLDivElement>('.base-cursor__button');
    if (button) {
      tickerState.targetButton = JSON.parse(JSON.stringify(button));
      if (!moduleState.button) {
        moduleState.button = button;
        moduleState.button.text = '';
      }
      if (tickerState.progress === 0) {
        tickerState.targetText = tickerState.targetButton?.text ?? '';
        updateButtonStyles();
        gsap.to(buttonEl as HTMLElement, {
          duration: 0.25,
          autoAlpha: 1,
          overwrite: true,
          delay: 0,
        });
        const buttonInner = container.value.querySelector<HTMLDivElement>('.base-cursor__button .util-button');
        gsap.to(buttonInner as HTMLElement, {
          paddingLeft: '',
          paddingRight: '',
          duration: 0.2,
          overwrite: true,
        });
        window.clearTimeout(fadeIconTimeout);
        fadeIconTimeout = window.setTimeout(fadeIcon, 10);
        const tickerDuration = tickerState.targetText.length * 0.03;
        void gsap
          .to(tickerState, {
            overwrite: true,
            duration: tickerDuration,
            progress: 1,
            delay: 0,
          })
          .then(() => checkLabels());
      } else {
        gsap.to(tickerState, {
          duration: 0.25,
          overwrite: true,
          progress: 0,
          onComplete: checkButton,
        });
      }
    } else {
      hideButton();
    }
  }

  function fadeIcon(opacity: number = 1) {
    if (!container.value) {
      return;
    }
    const buttonIcon = container.value.querySelector<HTMLDivElement>('.base-cursor__button .button-icon');
    if (buttonIcon) {
      gsap.to(buttonIcon as HTMLElement, {
        autoAlpha: opacity,
        duration: 0.2,
        overwrite: true,
      });
    }
  }

  let isHiding = false;

  function hideButton() {
    if (isHiding) {
      return;
    }
    isHiding = true;
    resetCursor();
    const buttonEl = container.value?.querySelector<HTMLDivElement>('.base-cursor__button');
    if (!buttonEl) {
      return;
    }
    const buttonInner = container.value?.querySelector<HTMLDivElement | HTMLButtonElement | HTMLAnchorElement>(
      '.base-cursor__button .util-button'
    );
    if (!buttonInner) {
      return;
    }
    gsap.to(buttonInner as HTMLElement, {
      paddingLeft: 0,
      paddingRight: 0,
      duration: 0.25,
      delay: 0.2,
      overwrite: true,
    });
    fadeIcon(0);
    gsap.to(buttonEl as HTMLElement, {
      autoAlpha: 0,
      duration: 0.25,
      delay: 0.25,
      overwrite: true,
      onComplete: clearButton,
    });
    gsap.to(tickerState, {
      duration: 0.25,
      progress: 0,
      overwrite: true,
    });
  }

  function onMouseOver(e: MouseEvent) {
    const parent = (e.target! as HTMLElement).closest<HTMLElement>('[data-cursor]');
    let parsedData: CursorData | null = null;
    try {
      parsedData = JSON.parse(parent?.getAttribute('data-cursor') ?? '{}') as CursorData;
      parsedData.labels = parsedData.labels?.map(label => label.trim()).filter(label => label.length > 0);

      if (!parent || !parsedData.button) {
        moduleState.currentTarget = undefined;
        resetCursor();
        hideButton();
        return;
      }
      if (parent === moduleState.currentTarget && parent !== null) {
        return;
      }
      isHiding = false;
      moduleState.currentTarget = parent;
      if (parsedData.button) {
        parsedData.button.theme = parsedData.button.theme ? parsedData.button.theme : 'primary';
      } else {
        return;
      }
      moduleState.parsedData = parsedData;
      void animateButton(parsedData.button ? parsedData.button : null);
      setCursor();
    } catch (error) {
      console.error('Invalid JSON', error);
    }
  }

  function onMouseMove(e: MouseEvent) {
    updatePosition({ x: e.clientX, y: e.clientY });
  }

  function onClick(e: any) {
    resetCursor();
    const hasButtonParent = (e?.target as HTMLElement).closest('button');
    if (hasButtonParent) {
      return;
    }
    if (tickerState.progress === 1 && tickerState.targetButton?.link) {
      visitLink(tickerState.targetButton?.link);
    }
  }

  function removeListeners() {
    document.documentElement.removeEventListener('click', onClick);
    document.documentElement.removeEventListener('mousemove', onMouseMove);
    document.documentElement.removeEventListener('mouseover', onMouseOver);
  }

  function addListeners() {
    removeListeners();
    document.documentElement.addEventListener('click', onClick);
    document.documentElement.addEventListener('mousemove', onMouseMove);
    document.documentElement.addEventListener('mouseover', onMouseOver);
  }

  watch(
    () => shouldFollow.value,
    () => {
      if (shouldFollow.value) {
        removeListeners();
        addListeners();
      } else {
        removeListeners();
      }
      if (!shouldFollow.value) {
        clearButton(true);
      }
    }
  );

  onUnmounted(() => {
    removeListeners();
  });

  // TODO can be remove as the watcher already takes care of it?
  onMounted(() => {
    if (shouldFollow.value) {
      addListeners();
    }
  });

  return { state: moduleState };
}
